package com.sweetlime.dungeonamigo.presentation.features.session

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.expandVertically
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ArrowForward
import androidx.compose.material.icons.filled.Casino
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.sweetlime.dungeonamigo.model.Condition
import com.sweetlime.dungeonamigo.model.DmEntity
import com.sweetlime.dungeonamigo.model.SessionParticipant
import com.sweetlime.dungeonamigo.presentation.features.dice.DiceRollerSheet
import com.sweetlime.dungeonamigo.presentation.features.dice.DiceViewModel
import com.sweetlime.dungeonamigo.presentation.ui.theme.BrandColors
import org.koin.compose.koinInject

data class DmDashboardScreen(val roomId: String) : Screen {
    @OptIn(ExperimentalMaterial3Api::class)
    @Composable
    override fun Content() {
        val navigator = LocalNavigator.currentOrThrow
        val vm: SessionViewModel = koinInject()
        val diceVm: DiceViewModel = koinInject()
        val state by vm.sessionState.collectAsState()
        val currentTurnIndex by vm.currentTurnIndex.collectAsState()

        val snackbarHostState = remember { SnackbarHostState() }

        var showEndConfirm by remember { mutableStateOf(false) }
        var showDiceSheet by remember { mutableStateOf(false) }
        var showAddEntitySheet by remember { mutableStateOf(false) }

        // Player initiative dialog
        var editInitiativeFor by remember { mutableStateOf<SessionParticipant?>(null) }
        // Player HP + condition dialogs
        var dmHpTargetFor by remember { mutableStateOf<SessionParticipant?>(null) }
        var conditionTargetFor by remember { mutableStateOf<SessionParticipant?>(null) }

        // Entity dialogs
        var editEntityInitiativeFor by remember { mutableStateOf<DmEntity?>(null) }
        var entityHpTargetFor by remember { mutableStateOf<DmEntity?>(null) }
        var conditionEntityTarget by remember { mutableStateOf<DmEntity?>(null) }

        var rollFeedExpanded by remember { mutableStateOf(false) }
        var defeatedExpanded by remember { mutableStateOf(false) }

        // Collect entity write errors and show as Snackbar
        LaunchedEffect(Unit) {
            vm.entityError.collect { message ->
                snackbarHostState.showSnackbar(message, duration = SnackbarDuration.Short)
            }
        }

        // Auto-post dice rolls to session feed; clean up on dispose (back button)
        DisposableEffect(Unit) {
            diceVm.onRollCompleted = { roll, label ->
                if (vm.sessionState.value is SessionState.InSession) {
                    vm.postRollToFeed(roll, label)
                }
            }
            onDispose {
                diceVm.onRollCompleted = null
                vm.leaveRoom()
            }
        }

        val shouldPop = state is SessionState.Idle || state is SessionState.Error
        LaunchedEffect(shouldPop) {
            if (shouldPop) navigator.pop()
        }

        val session = state as? SessionState.InSession ?: return
        val defeatedEntities = session.dmEntities.filter { it.isDefeated }

        Scaffold(
            containerColor = BrandColors.Background,
            snackbarHost = {
                SnackbarHost(snackbarHostState) { data ->
                    Snackbar(
                        snackbarData = data,
                        containerColor = BrandColors.Ember,
                        contentColor = BrandColors.TextPrimary,
                    )
                }
            },
        ) { innerPadding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
                .background(BrandColors.Background),
        ) {
            LazyColumn(
                modifier = Modifier.fillMaxSize(),
                contentPadding = PaddingValues(bottom = 200.dp),
            ) {
                // ── Top Bar ──────────────────────────────────────────────────
                item {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .background(BrandColors.Surface)
                            .padding(horizontal = 16.dp, vertical = 12.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween,
                    ) {
                        Column {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(8.dp),
                            ) {
                                Text("Session:", color = BrandColors.TextSecondary, fontSize = 14.sp)
                                RoomCodeBadge(session.room.roomCode)
                            }
                            if (session.room.campaignName.isNotBlank()) {
                                Text(session.room.campaignName, color = BrandColors.TextSecondary, fontSize = 11.sp)
                            }
                        }
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                        ) {
                            Box(
                                modifier = Modifier
                                    .clip(RoundedCornerShape(8.dp))
                                    .background(BrandColors.SurfaceLight)
                                    .padding(horizontal = 8.dp, vertical = 4.dp),
                            ) {
                                Text(
                                    "${session.participants.count { !it.isDm }} players",
                                    color = BrandColors.TextSecondary,
                                    fontSize = 11.sp,
                                )
                            }
                            Button(
                                onClick = { showEndConfirm = true },
                                colors = ButtonDefaults.buttonColors(containerColor = BrandColors.Ember),
                                shape = RoundedCornerShape(8.dp),
                                contentPadding = PaddingValues(horizontal = 12.dp, vertical = 6.dp),
                            ) {
                                Text("End Session", color = BrandColors.TextPrimary, fontSize = 12.sp)
                            }
                        }
                    }
                }

                // ── Initiative Tracker ───────────────────────────────────────
                item {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 16.dp, vertical = 12.dp),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Text(
                            "Initiative Order",
                            color = BrandColors.TextPrimary,
                            fontWeight = FontWeight.Bold,
                            fontSize = 16.sp,
                        )
                        OutlinedButton(
                            onClick = vm::requestAllInitiativeRolls,
                            border = BorderStroke(1.dp, BrandColors.Parchment.copy(alpha = 0.4f)),
                            colors = ButtonColors(
                                containerColor = Color.Transparent,
                                contentColor = BrandColors.Parchment,
                                disabledContainerColor = Color.Transparent,
                                disabledContentColor = BrandColors.TextSecondary,
                            ),
                            contentPadding = PaddingValues(horizontal = 10.dp, vertical = 4.dp),
                            shape = RoundedCornerShape(8.dp),
                        ) {
                            Text("🎲 Roll All", fontSize = 12.sp)
                        }
                    }
                }

                if (session.initiativeOrder.isEmpty()) {
                    item {
                        Text(
                            "Roll initiative to begin combat",
                            color = BrandColors.TextSecondary,
                            fontSize = 13.sp,
                            modifier = Modifier.padding(horizontal = 16.dp),
                        )
                    }
                }

                itemsIndexed(session.initiativeOrder) { index, entry ->
                    val orderSize = session.initiativeOrder.size.coerceAtLeast(1)
                    val isActiveTurn = index == currentTurnIndex % orderSize
                    when (entry) {
                        is InitiativeEntry.PlayerEntry -> InitiativeRow(
                            participant = entry.participant,
                            isActiveTurn = isActiveTurn,
                            onEditInitiative = { editInitiativeFor = entry.participant },
                            modifier = Modifier.padding(horizontal = 16.dp, vertical = 2.dp),
                        )
                        is InitiativeEntry.EntityEntry -> EntityInitiativeRow(
                            entity = entry.entity,
                            isActiveTurn = isActiveTurn,
                            onHpDecrement = {
                                vm.updateEntityHp(
                                    entry.entity.id,
                                    (entry.entity.currentHp - 1).coerceAtLeast(0),
                                    entry.entity.temporaryHp,
                                )
                            },
                            onHpIncrement = {
                                vm.updateEntityHp(
                                    entry.entity.id,
                                    (entry.entity.currentHp + 1).coerceAtMost(entry.entity.maxHp),
                                    entry.entity.temporaryHp,
                                )
                            },
                            onHpLongPress = { entityHpTargetFor = entry.entity },
                            onEditInitiative = { editEntityInitiativeFor = entry.entity },
                            onConditions = { conditionEntityTarget = entry.entity },
                            onToggleDefeated = { vm.toggleEntityDefeated(entry.entity.id) },
                            onRemove = { vm.removeEntity(entry.entity.id) },
                            modifier = Modifier.padding(horizontal = 16.dp, vertical = 2.dp),
                        )
                    }
                }

                // ── Defeated Entities ────────────────────────────────────────
                if (defeatedEntities.isNotEmpty()) {
                    item {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable { defeatedExpanded = !defeatedExpanded }
                                .padding(horizontal = 16.dp, vertical = 10.dp),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Text(
                                "Defeated (${defeatedEntities.size})",
                                color = BrandColors.TextSecondary,
                                fontWeight = FontWeight.SemiBold,
                                fontSize = 14.sp,
                            )
                            Text(
                                if (defeatedExpanded) "▲" else "▼",
                                color = BrandColors.TextSecondary,
                                fontSize = 11.sp,
                            )
                        }
                    }
                    item {
                        AnimatedVisibility(
                            visible = defeatedExpanded,
                            enter = expandVertically(),
                            exit = shrinkVertically(),
                        ) {
                            Column(
                                modifier = Modifier.padding(horizontal = 16.dp),
                                verticalArrangement = Arrangement.spacedBy(4.dp),
                            ) {
                                defeatedEntities.forEach { entity ->
                                    DefeatedEntityRow(
                                        entity = entity,
                                        onRevive = { vm.toggleEntityDefeated(entity.id) },
                                        onRemove = { vm.removeEntity(entity.id) },
                                    )
                                }
                            }
                        }
                    }
                }

                // ── Party Cards ──────────────────────────────────────────────
                item {
                    Text(
                        "Party",
                        color = BrandColors.TextPrimary,
                        fontWeight = FontWeight.Bold,
                        fontSize = 16.sp,
                        modifier = Modifier.padding(horizontal = 16.dp, vertical = 12.dp),
                    )
                }

                items(session.participants.filter { !it.isDm }) { participant ->
                    PartyMemberCard(
                        participant = participant,
                        modifier = Modifier.padding(horizontal = 16.dp, vertical = 4.dp),
                        showDmControls = true,
                        onHpTap = { dmHpTargetFor = participant },
                        onConditionAdd = { conditionTargetFor = participant },
                        onKick = { vm.kickParticipant(participant.userId) },
                    )
                }

                // ── Roll Feed (collapsible) ──────────────────────────────────
                item {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clickable { rollFeedExpanded = !rollFeedExpanded }
                            .padding(horizontal = 16.dp, vertical = 12.dp),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Text("Roll Feed", color = BrandColors.TextPrimary, fontWeight = FontWeight.Bold, fontSize = 16.sp)
                        Text(
                            if (rollFeedExpanded) "▲ collapse" else "▼ expand",
                            color = BrandColors.TextSecondary,
                            fontSize = 11.sp,
                        )
                    }
                }

                item {
                    AnimatedVisibility(
                        visible = rollFeedExpanded,
                        enter = expandVertically(),
                        exit = shrinkVertically(),
                    ) {
                        Column(
                            modifier = Modifier.padding(horizontal = 16.dp),
                            verticalArrangement = Arrangement.spacedBy(4.dp),
                        ) {
                            if (session.rollFeed.isEmpty()) {
                                Text("No rolls yet", color = BrandColors.TextSecondary, fontSize = 13.sp)
                            } else {
                                session.rollFeed.forEach { roll -> RollFeedRow(roll = roll) }
                            }
                        }
                    }
                    if (!rollFeedExpanded) {
                        Column(
                            modifier = Modifier.padding(horizontal = 16.dp),
                            verticalArrangement = Arrangement.spacedBy(4.dp),
                        ) {
                            session.rollFeed.take(5).forEach { roll -> RollFeedRow(roll = roll) }
                        }
                    }
                }
            }

            // ── FABs (bottom-end, stacked) ───────────────────────────────────

            // Next Turn FAB
            FloatingActionButton(
                onClick = vm::advanceTurn,
                modifier = Modifier
                    .align(Alignment.BottomEnd)
                    .padding(end = 16.dp, bottom = 144.dp),
                containerColor = BrandColors.Ember,
            ) {
                Icon(Icons.Filled.ArrowForward, "Next Turn", tint = BrandColors.TextPrimary)
            }

            // Add Entity FAB
            FloatingActionButton(
                onClick = { showAddEntitySheet = true },
                modifier = Modifier
                    .align(Alignment.BottomEnd)
                    .padding(end = 16.dp, bottom = 80.dp),
                containerColor = BrandColors.Parchment,
            ) {
                Icon(Icons.Filled.Add, "Add Entity", tint = BrandColors.Background)
            }

            // Dice FAB
            FloatingActionButton(
                onClick = { showDiceSheet = true },
                modifier = Modifier
                    .align(Alignment.BottomEnd)
                    .padding(end = 16.dp, bottom = 16.dp),
                containerColor = BrandColors.SurfaceLight,
            ) {
                Icon(Icons.Filled.Casino, "Roll Dice", tint = BrandColors.Parchment)
            }
        }
        } // end Scaffold

        // ── Dialogs & Sheets ─────────────────────────────────────────────────

        if (showEndConfirm) {
            AlertDialog(
                onDismissRequest = { showEndConfirm = false },
                containerColor = BrandColors.Surface,
                title = { Text("End Session?", color = BrandColors.TextPrimary, fontWeight = FontWeight.Bold) },
                text = { Text("This will close the room for all players.", color = BrandColors.TextSecondary) },
                confirmButton = {
                    TextButton(onClick = {
                        vm.endSession()
                        showEndConfirm = false
                    }) {
                        Text("End Session", color = BrandColors.Ember, fontWeight = FontWeight.Bold)
                    }
                },
                dismissButton = {
                    TextButton(onClick = { showEndConfirm = false }) {
                        Text("Cancel", color = BrandColors.TextSecondary)
                    }
                },
            )
        }

        if (showDiceSheet) {
            DiceRollerSheet(vm = diceVm, onDismiss = { showDiceSheet = false })
        }

        if (showAddEntitySheet) {
            AddDmEntitySheet(vm = vm, onDismiss = { showAddEntitySheet = false })
        }

        // Player initiative dialog
        editInitiativeFor?.let { target ->
            EditInitiativeDialog(
                participant = target,
                onApply = { value ->
                    vm.setParticipantInitiative(target.userId, value)
                    editInitiativeFor = null
                },
                onDismiss = { editInitiativeFor = null },
            )
        }

        // Player HP edit dialog
        dmHpTargetFor?.let { target ->
            SetHpDialog(
                current = target.currentHp,
                max = target.maxHp,
                temp = target.temporaryHp,
                onApply = { newHp, newTemp ->
                    vm.updateParticipantHp(target.userId, newHp, newTemp)
                    dmHpTargetFor = null
                },
                onDismiss = { dmHpTargetFor = null },
            )
        }

        // Player condition dialog
        conditionTargetFor?.let { target ->
            ConditionPickerDialog(
                activeConditions = target.conditions,
                onApply = { conditions ->
                    vm.updateParticipantConditions(target.userId, conditions)
                    conditionTargetFor = null
                },
                onDismiss = { conditionTargetFor = null },
            )
        }

        // Entity initiative dialog
        editEntityInitiativeFor?.let { target ->
            EditEntityInitiativeDialog(
                entity = target,
                onApply = { value ->
                    vm.setEntityInitiative(target.id, value)
                    editEntityInitiativeFor = null
                },
                onDismiss = { editEntityInitiativeFor = null },
            )
        }

        // Entity HP edit dialog
        entityHpTargetFor?.let { target ->
            SetHpDialog(
                current = target.currentHp,
                max = target.maxHp,
                temp = target.temporaryHp,
                onApply = { newHp, newTemp ->
                    vm.updateEntityHp(target.id, newHp, newTemp)
                    entityHpTargetFor = null
                },
                onDismiss = { entityHpTargetFor = null },
            )
        }

        // Entity condition dialog
        conditionEntityTarget?.let { target ->
            ConditionPickerDialog(
                activeConditions = target.conditions,
                onApply = { conditions ->
                    vm.updateEntityConditions(target.id, conditions)
                    conditionEntityTarget = null
                },
                onDismiss = { conditionEntityTarget = null },
            )
        }
    }
}

// ── Initiative Rows ───────────────────────────────────────────────────────────

@Composable
private fun InitiativeRow(
    participant: SessionParticipant,
    isActiveTurn: Boolean,
    onEditInitiative: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val bgColor = if (isActiveTurn) BrandColors.SurfaceLight else BrandColors.Surface

    Box(modifier = modifier.fillMaxWidth().clip(RoundedCornerShape(10.dp))) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(bgColor)
                .padding(horizontal = 12.dp, vertical = 10.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            Text(if (isActiveTurn) "▶" else "  ", color = BrandColors.Ember, fontSize = 12.sp)

            Column(Modifier.weight(1f)) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                    if (participant.isDm) Text("👑", fontSize = 10.sp)
                    Text(
                        participant.characterName.ifBlank { participant.displayName },
                        color = BrandColors.TextPrimary,
                        fontWeight = FontWeight.SemiBold,
                        fontSize = 14.sp,
                    )
                }
                if (participant.characterClass.isNotBlank()) {
                    Text(participant.characterClass, color = BrandColors.TextSecondary, fontSize = 11.sp)
                }
            }

            HpBar(participant.currentHp, participant.maxHp, Modifier.width(60.dp))
            Text("${participant.currentHp}/${participant.maxHp}", color = BrandColors.TextSecondary, fontSize = 10.sp)
            Text("AC${participant.armorClass}", color = BrandColors.SpellBlue, fontSize = 11.sp)

            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(6.dp))
                    .background(BrandColors.Background)
                    .clickable(onClick = onEditInitiative)
                    .padding(horizontal = 8.dp, vertical = 4.dp),
            ) {
                Text("${participant.initiativeRoll}", color = BrandColors.Parchment, fontSize = 14.sp, fontWeight = FontWeight.Bold)
            }
        }

        if (isActiveTurn) {
            Box(modifier = Modifier.width(3.dp).fillMaxHeight().background(BrandColors.Ember))
        }
    }
}

@Composable
private fun EntityInitiativeRow(
    entity: DmEntity,
    isActiveTurn: Boolean,
    onHpDecrement: () -> Unit,
    onHpIncrement: () -> Unit,
    onHpLongPress: () -> Unit,
    onEditInitiative: () -> Unit,
    onConditions: () -> Unit,
    onToggleDefeated: () -> Unit,
    onRemove: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val accentColor = if (isActiveTurn) BrandColors.Ember else BrandColors.TextSecondary

    Box(modifier = modifier.fillMaxWidth().clip(RoundedCornerShape(10.dp))) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .background(BrandColors.SurfaceLight)
                .padding(horizontal = 12.dp, vertical = 10.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            // Top row: turn indicator, skull, name + type, AC, initiative
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Text(if (isActiveTurn) "▶" else "  ", color = BrandColors.Ember, fontSize = 12.sp)
                Text("☠", color = BrandColors.TextSecondary, fontSize = 14.sp)

                Column(Modifier.weight(1f)) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(6.dp),
                    ) {
                        Text(entity.name, color = BrandColors.TextPrimary, fontWeight = FontWeight.SemiBold, fontSize = 14.sp)
                        if (entity.type.isNotBlank()) {
                            EntityTypeBadge(entity.type)
                        }
                    }
                }

                Text("AC${entity.armorClass}", color = BrandColors.SpellBlue, fontSize = 11.sp)

                // Initiative — tappable to edit
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(6.dp))
                        .background(BrandColors.Background)
                        .clickable(onClick = onEditInitiative)
                        .padding(horizontal = 8.dp, vertical = 4.dp),
                ) {
                    Text("${entity.initiativeRoll}", color = BrandColors.Parchment, fontSize = 14.sp, fontWeight = FontWeight.Bold)
                }
            }

            // HP tracker
            EntityHpTracker(
                current = entity.currentHp,
                max = entity.maxHp,
                temp = entity.temporaryHp,
                onDecrement = onHpDecrement,
                onIncrement = onHpIncrement,
                onLongPress = onHpLongPress,
            )

            // Conditions + action row
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween,
            ) {
                // Condition chips
                Row(
                    modifier = Modifier.weight(1f),
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    entity.conditions.take(3).forEach { condName ->
                        val cond = Condition.entries.find { it.name == condName }
                        Text(
                            "${cond?.emoji ?: ""}${cond?.displayName ?: condName}",
                            color = BrandColors.Ember,
                            fontSize = 10.sp,
                            modifier = Modifier
                                .clip(RoundedCornerShape(8.dp))
                                .background(BrandColors.Ember.copy(alpha = 0.12f))
                                .padding(horizontal = 5.dp, vertical = 2.dp),
                        )
                    }
                    if (entity.conditions.size > 3) {
                        Text("+${entity.conditions.size - 3}", color = BrandColors.TextSecondary, fontSize = 10.sp)
                    }
                    // Condition add button
                    Box(
                        modifier = Modifier
                            .clip(CircleShape)
                            .background(BrandColors.Background)
                            .clickable(onClick = onConditions)
                            .padding(3.dp),
                    ) {
                        Icon(Icons.Filled.Add, "Add condition", tint = BrandColors.TextSecondary, modifier = Modifier.size(12.dp))
                    }
                }

                // Defeat / Remove buttons
                Row(horizontalArrangement = Arrangement.spacedBy(2.dp)) {
                    TextButton(
                        onClick = onToggleDefeated,
                        contentPadding = PaddingValues(horizontal = 6.dp, vertical = 2.dp),
                    ) {
                        Text("☠ Defeat", color = BrandColors.TextSecondary, fontSize = 11.sp)
                    }
                    TextButton(
                        onClick = onRemove,
                        contentPadding = PaddingValues(horizontal = 6.dp, vertical = 2.dp),
                    ) {
                        Text("✕", color = BrandColors.Ember, fontSize = 11.sp)
                    }
                }
            }
        }

        // Left accent border
        Box(modifier = Modifier.width(3.dp).fillMaxHeight().background(accentColor))
    }
}

@Composable
private fun EntityTypeBadge(type: String) {
    Box(
        modifier = Modifier
            .clip(RoundedCornerShape(8.dp))
            .background(BrandColors.TextSecondary.copy(alpha = 0.15f))
            .padding(horizontal = 5.dp, vertical = 1.dp),
    ) {
        Text(type, color = BrandColors.TextSecondary, fontSize = 10.sp)
    }
}

@Composable
private fun DefeatedEntityRow(
    entity: DmEntity,
    onRevive: () -> Unit,
    onRemove: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .alpha(0.4f)
            .clip(RoundedCornerShape(8.dp))
            .background(BrandColors.Surface)
            .padding(horizontal = 12.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Column(Modifier.weight(1f)) {
            Text(
                entity.name,
                color = BrandColors.TextSecondary,
                fontSize = 13.sp,
                textDecoration = TextDecoration.LineThrough,
            )
            if (entity.type.isNotBlank()) {
                Text(entity.type, color = BrandColors.TextSecondary, fontSize = 11.sp)
            }
        }
        Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
            TextButton(
                onClick = onRevive,
                contentPadding = PaddingValues(horizontal = 6.dp, vertical = 2.dp),
            ) {
                Text("Revive", color = BrandColors.SpellBlue, fontSize = 11.sp)
            }
            TextButton(
                onClick = onRemove,
                contentPadding = PaddingValues(horizontal = 6.dp, vertical = 2.dp),
            ) {
                Text("Remove", color = BrandColors.Ember, fontSize = 11.sp)
            }
        }
    }
}

// ── Dialogs ───────────────────────────────────────────────────────────────────

@Composable
private fun EditInitiativeDialog(
    participant: SessionParticipant,
    onApply: (Int) -> Unit,
    onDismiss: () -> Unit,
) {
    var input by remember { mutableStateOf("${participant.initiativeRoll}") }

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = BrandColors.Surface,
        title = {
            Text(
                "Set Initiative — ${participant.characterName.ifBlank { participant.displayName }}",
                color = BrandColors.TextPrimary,
                fontWeight = FontWeight.Bold,
            )
        },
        text = {
            OutlinedTextField(
                value = input,
                onValueChange = { v -> input = v.filter { it.isDigit() || it == '-' } },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                placeholder = { Text("Initiative roll", color = BrandColors.TextSecondary) },
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = BrandColors.Parchment,
                    unfocusedBorderColor = BrandColors.SurfaceLight,
                    focusedTextColor = BrandColors.TextPrimary,
                    unfocusedTextColor = BrandColors.TextPrimary,
                    cursorColor = BrandColors.Parchment,
                ),
                shape = RoundedCornerShape(8.dp),
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
            )
        },
        confirmButton = {
            TextButton(onClick = {
                val value = input.toIntOrNull() ?: return@TextButton
                onApply(value)
            }) {
                Text("Set", color = BrandColors.Parchment, fontWeight = FontWeight.Bold)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text("Cancel", color = BrandColors.TextSecondary) }
        },
    )
}

@Composable
private fun EditEntityInitiativeDialog(
    entity: DmEntity,
    onApply: (Int) -> Unit,
    onDismiss: () -> Unit,
) {
    var input by remember { mutableStateOf("${entity.initiativeRoll}") }

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = BrandColors.Surface,
        title = {
            Text(
                "Set Initiative — ${entity.name}",
                color = BrandColors.TextPrimary,
                fontWeight = FontWeight.Bold,
            )
        },
        text = {
            OutlinedTextField(
                value = input,
                onValueChange = { v -> input = v.filter { it.isDigit() || it == '-' } },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                placeholder = { Text("Initiative roll", color = BrandColors.TextSecondary) },
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = BrandColors.Parchment,
                    unfocusedBorderColor = BrandColors.SurfaceLight,
                    focusedTextColor = BrandColors.TextPrimary,
                    unfocusedTextColor = BrandColors.TextPrimary,
                    cursorColor = BrandColors.Parchment,
                ),
                shape = RoundedCornerShape(8.dp),
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
            )
        },
        confirmButton = {
            TextButton(onClick = {
                val value = input.toIntOrNull() ?: return@TextButton
                onApply(value)
            }) {
                Text("Set", color = BrandColors.Parchment, fontWeight = FontWeight.Bold)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text("Cancel", color = BrandColors.TextSecondary) }
        },
    )
}
